<template>
  <section
    :class="[
      'wrapper',
      'content',
      'content--centered',
      $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
      $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
    ]"
  >
    <am2-ask-moshtix-access-token-modal
      :is-show="showAskMoshtixAccessTokenModal"
      :loading="isCreatingMoshtixIntegration"
      @confirm="handleMoshtixAccessTokenConfirm"
      @cancel="handleMoshtixAccessTokenCancel"
    />
    <am2-step-link
      class="step-back-link"
      text="Back to integrations"
      @click="handleBackLinkClick" />
    <am2-opt-in-settings-modal
      :is-show="showOptinSettingsModal"
      @cancel="showOptinSettingsModal = false"
      providerName="Moshtix"
      :message-list-oid="integrationMessageListOid"
    />
    <div
      :class="[
        'title-section',
        $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
      ]">
      <div class="title-section-left">
        <am2-icon-title-section
          title="Moshtix"
          description="Automatically sync your ticket sales, events & attendees from Moshtix"
          :icon-props="{
            name: 'moshtix',
            color: $arStyle.color.moshtix,
            height: '46px',
            width: '40px',
          }"
        />
      </div>
      <div class="title-section-right" v-if="!isFetchingMoshtixIntegration && integrationsSummary">
        <am2-integration-account-select
          v-if="!isFetchingMoshtixIntegration && moshtixIntegration && currentIntegration"
          :default-selected-integration-oid="currentIntegration.oid"
          :available-integrations="integrationsSummary"
          @select="handleIntegrationChange"
          @new-account="handleAddNewIntegration"
          :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
        />
        <am2-expand-button-dropdown
          align="left"
          :button-props="{ iconName: 'settings' }"
          :items="expandButtonItems"
          @select="handleSettingOptionSelect" />
      </div>
    </div>
    <am2-tabs
      :items="[
        { title: 'Notifications' },
        { title: 'Status' }
      ]"
      :selected-tab-index="selectedTabIndex"
      class="tab-options"
      @select="(selected, idx) => selectedTabIndex = idx"
    />
    <div
      v-if="selectedTabIndex === 0"
    >
      <NotificationsContainer
        :integration="currentIntegration"
        :loading="isFetchingMoshtixIntegration"
      />
    </div>

    <div
      v-if="selectedTabIndex === 1"
      class="integration-status-container"
    >
      <section v-if="isFetchingMoshtixIntegration" class="integration-loading-container">
        <am2-loading-bubble />
      </section>
      <section v-else>
        <section v-if="integrationFailed" class="integration-failed-message">
          <ar-snackbar
            type="warning"
            message="Your Moshtix connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
            @anchorClick="handleReconnectIntegration"
            :style="{ width: '100%' }"
          />
        </section>
        <SyncBlockContainer
          v-else
          class="sync-block-container"
          :is-fetching-sync-task="isFetchingSyncTask"
          :sync-status="syncStatus"
          :sync-time="syncTime"
          :sync-copies="{
            'in-progress': 'Syncing data from Moshtix',
            'pending': 'The data sync is still pending',
            'completed': 'Your Moshtix account is currently connected',
            'cancelled': 'Your Moshtix account connection is cancelled',
            'failed': syncErrorMessage,
            'default': 'Syncing tasks not found',
          }"
          @startSync="handleStartSync"
          @stopSync="handleStopSync"
        />
      </section>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import SyncBlockContainer from '../components/sync-block-container';
import NotificationsContainer from '../components/notifications-container';

export default {
  name: 'MoshtixIntegration',
  layout: 'default',
  components: {
    SyncBlockContainer,
    NotificationsContainer
  },

  data: () => ({
    currentlySelectedIntegrationIndex: 0,
    syncStatusPoller: null,
    showAskMoshtixAccessTokenModal: false,
    isReconnectingAccount: false,
    selectedTabIndex: 1,
    showOptinSettingsModal: false
  }),

  computed: {
    ...mapState({
      moshtixIntegration: state => state.moshtixIntegration.integration,
      isCreatingMoshtixIntegration: state => state.moshtixIntegration.isCreatingIntegration,
      isFetchingMoshtixIntegration: state => state.moshtixIntegration.isFetchingIntegration,
      isDeletingMoshtixIntegration: state => state.moshtixIntegration.isDeletingIntegration,
      isFetchingSyncTask: state => state.moshtixIntegration.isFetchingSyncTask,
      syncTask: state => state.moshtixIntegration.syncTask,
      syncStatus: state => state.moshtixIntegration.syncStatus,
    }),

    moshtixIntegrationEnabled() {
      return process.env.arEnableMoshtixIntegration;
    },

    syncErrorMessage() {
      return this.syncTask?.statusDetails?.lastError || null;
    },

    syncTime() {
      return this.syncTask?.sysMtime || null;
    },

    integrationsSummary() {
      if (!this.moshtixIntegration) return null;
      return this.moshtixIntegration
        .filter(i => i.status !== 'failed')
        .map(i => ({
          oid: i.oid,
          name: `${i.integration?.user?.firstName} ${i.integration?.user?.lastName}`,
          email: i.integration?.user?.username,
        }));
    },

    currentIntegration() {
      if (this.currentlySelectedIntegrationIndex === null) return null;
      if (!this.moshtixIntegration || this.moshtixIntegration.length < 1 || !this.moshtixIntegration[this.currentlySelectedIntegrationIndex]) return null;
      return this.moshtixIntegration[this.currentlySelectedIntegrationIndex];
    },

    integrationMessageListOid() {
      return this.currentIntegration ? this.currentIntegration?.meta?.messageListOid : null;
    },

    expandButtonItems() {
      let i = [];
      i.push({
        name: 'Moshtix Settings',
        value: 'openSettingsModal'
      });
      i.push({
        name: 'Open Moshtix',
        value: 'goToMoshtixSite'
      });
      i.push({
        name: 'Reconnect Integration',
        value: 'reconnectIntegration'
      });
      i.push({
        name: 'Remove Account',
        value: 'removeIntegration'
      })
      return i;
    },

    integrationFailed() {
      if (!this.currentIntegration) return false;
      return this.currentIntegration.status === 'failed';
    }
  },

  async mounted() {
    if (!this.moshtixIntegrationEnabled) {
      this.handleBackLinkClick();
    }
    await this.fetchIntegration();
    this.startFetchTasksPolling();
  },

  beforeDestroy() {
    this['moshtixIntegration/RESET_INTEGRATION']();

    if (this.syncStatusPoller) {
      clearInterval(this.syncStatusPoller);
    }
  },

  methods: {
    ...mapActions([
      'SHOW_CONFIRM',
      'moshtixIntegration/CREATE_MOSHTIX_INTEGRATION',
      'moshtixIntegration/FETCH_INTEGRATION',
      'moshtixIntegration/DELETE_INTEGRATION',
      'moshtixIntegration/SYNC_START',
      'moshtixIntegration/SYNC_STOP',
      'moshtixIntegration/FETCH_SYNC_TASK',
      'CONNECT_TO_INTEGRATION',
      'promoterTasks/START_POLLING_PENDING_TASKS',
    ]),
    ...mapMutations([
      'moshtixIntegration/RESET_INTEGRATION',
    ]),
    startFetchTasksPolling() {
      if (this.currentIntegration?.oid && !this.isFetchingSyncTask) {
        this.fetchTasks();
        this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
      }
    },
    async fetchIntegration() {
      await this['moshtixIntegration/FETCH_INTEGRATION']();
      if (!this.moshtixIntegration || this.moshtixIntegration.length === 0) {
        this.$router.push({
          path: '/settings/integrations',
        });
      }
    },

    async fetchTasks() {
      await this['moshtixIntegration/FETCH_SYNC_TASK'](this.currentIntegration.oid);

      if (this.currentIntegration && this.hasExistingTask && this.syncStatus !== "in-progress") {
        window.clearInterval(this.syncStatusPoller);
        this.syncStatusPoller = null;
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }
    },

    connectToMoshtix() {
      this.showAskMoshtixAccessTokenModal = true;
    },

    hasExistingTask() {
      return this.syncTask !== null;
    },

    handleBackLinkClick() {
      this.$router.push({ path: '/settings/integrations' });
    },

    handleIntegrationChange(integration, index) {
      this.currentlySelectedIntegrationIndex = index;
      this.fetchTasks();
    },

    async handleAddNewIntegration() {
      this.connectToMoshtix();
    },

    async handleStartSync() {
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
      await this['moshtixIntegration/SYNC_START']({ oid: this.currentIntegration.oid });
      if (this.syncStatus !== 'completed') {
        this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
      }
    },

    handleStopSync() {
      this['moshtixIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
    },

    async handleReconnectIntegration() {
      this.isReconnectingAccount = true;
      this.connectToMoshtix();
    },

    handleMoshtixAccessTokenCancel() {
      if (this.isReconnectingAccount) {
        this.isReconnectingAccount = false;
      }
      this.showAskMoshtixAccessTokenModal = false;
    },

    async handleMoshtixAccessTokenConfirm({ username, password, accessToken }) {
      if (this.isReconnectingAccount && username !== this.currentIntegration.integration.user.username) {
        this.$arNotification.push({
          type: 'error',
          message: 'The username you put here is different to the username of the account you are trying to reconnect.',
        });
        return;
      }
      let succeed;
      if (this.isReconnectingAccount) {
        succeed = await this['moshtixIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
        if (!succeed) {
          return;
        }
      }
      succeed = await this['moshtixIntegration/CREATE_MOSHTIX_INTEGRATION']({
        username,
        password,
        accessToken
      });
      if (succeed) {
        await this.fetchIntegration();
        this.currentlySelectedIntegrationIndex = this.moshtixIntegration.length - 1;
        this.showAskMoshtixAccessTokenModal = false;
        this.startFetchTasksPolling();
      } else {
        this.currentlySelectedIntegrationIndex = 0;
      }
    },

    async handleSettingOptionSelect(item) {
      if (item.value === 'removeIntegration') {
        const answer = await this.SHOW_CONFIRM({
          messageHtml: 'Removing the Moshtix integration will stop syncing your orders and customer data from Moshtix.',
          confirmButtonText: 'Remove integration',
          iconName: 'alert-question-mark',
          title: 'Are you sure?',
        });
        if (answer) {
          try {
            await this['moshtixIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
            if (this.moshtixIntegration.length > 0) {
              this.currentlySelectedIntegrationIndex = 0;
              await this.fetchIntegration();
              this.startFetchTasksPolling();
            } else {
              this.$router.push({ path: '/settings/integrations' });
            }
          } catch (e) {
            this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
          }
        }
      } else if (item.value === 'reconnectIntegration') {
        this.handleReconnectIntegration();
      } else if (item.value === 'goToMoshtixSite') {
        if (!this.currentIntegration) return;
        var win = window.open('https://admin.moshtix.com/v2/clientlogin', '_blank');
        win.focus();
      } else if (item.value == 'openSettingsModal') {
        if (!this.currentIntegration) return;
        this.showOptinSettingsModal = true;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.wrapper {

  .tab-options {
    margin: 60px 0 20px 0;
    border-bottom: 1px $skyBlueGrey500 solid;
  }

  &.sm-max {
    padding: 0 24px;
  }
  &.xs-max {
    padding: 0 12px;
  }

  .integration-loading-container {
    display: flex;
    justify-content: center;
    margin-top: 65px;
  }


  .step-back-link {
    margin-top: 48px;
  }

  .title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 48px;

    .title-section-left {
      display: inline-flex;
      align-items: flex-start;

      .tag {
        position: relative;
        margin-left: 10px;
        top: 4px;
      }
    }

    .title-section-right {
      display: inline-flex;
      align-items: center;

      .select {
        width: 300px;
        margin-right: 10px;
      }
    }

    &.md-max {
      flex-direction: column;
      align-items: flex-start;

      .title-section-right {
        margin-top:16px;
      }
    }
  }

  .sync-block-container {
    margin-top:0px;
  }
}
</style>
